import React, { useState } from "react";
import { Building, Compass, FileText, Layers, TreePine, Landmark, ArrowRight, ShieldAlert, CircleCheck as CheckCircle2, Maximize2 } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
export default function DecaturHomeAdditionsGuide() {
  const [activeTab, setActiveTab] = useState<"types" | "permitting" | "historic">("types");
  return <section id="decaturhomeadditionsguide" data-nav="light" className="relative bg-background text-foreground py-20 md:py-28 overflow-hidden">
      {/* Background Subtle Accent Texture */}
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none opacity-40 bg-[radial-gradient(ellipse_at_top_right,var(--border)_0%,transparent_60%)]" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mb-12 lg:mb-16">
          <Badge variant="outline" className="mb-4 text-xs tracking-wider uppercase border-border/80 bg-secondary/40 text-foreground/80">
            Planning & Architecture Guide
          </Badge>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-foreground leading-[1.15] mb-5">
            Planning a Home Addition in Decatur: Architectural Styles, Zoning, and Historic Standards
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Expanding an established home in Decatur requires balancing historic character, structural integration, and strict local municipal requirements. Whether adapting a 1920s bungalow or expanding a mid-century brick ranch, understanding site dynamics early ensures a coherent and enduring design.
          </p>
        </div>

        {/* Category Navigation Controls */}
        <div className="flex flex-wrap gap-2 sm:gap-3 p-1.5 bg-secondary/50 rounded-xl border border-border/60 w-fit mb-12">
          <Button variant={activeTab === "types" ? "default" : "ghost"} size="sm" onClick={() => setActiveTab("types")} className="rounded-lg text-xs sm:text-sm font-medium transition-all">
            <Layers className="size-4 mr-2" />
            Addition Types & Architectural Fit
          </Button>
          <Button variant={activeTab === "permitting" ? "default" : "ghost"} size="sm" onClick={() => setActiveTab("permitting")} className="rounded-lg text-xs sm:text-sm font-medium transition-all">
            <FileText className="size-4 mr-2" />
            City of Decatur vs. DeKalb County
          </Button>
          <Button variant={activeTab === "historic" ? "default" : "ghost"} size="sm" onClick={() => setActiveTab("historic")} className="rounded-lg text-xs sm:text-sm font-medium transition-all">
            <Landmark className="size-4 mr-2" />
            Historic District Guidelines
          </Button>
        </div>

        {/* Tab 1: Addition Types & Architectural Integration */}
        {activeTab === "types" && <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-8 space-y-8">
              {/* Second Story Additions */}
              <Card className="bg-card/90 border border-border/70 rounded-xl shadow-xs">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2 text-primary font-medium text-xs uppercase tracking-wider mb-1">
                    <Maximize2 className="size-4" /> Vertical Expansion
                  </div>
                  <CardTitle className="font-serif text-2xl text-foreground font-normal">
                    Second-Story Additions & Full Pop-Tops
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 text-sm sm:text-base text-muted-foreground leading-relaxed">
                  <p>
                    Full second-story additions and partial upper-level extensions are common solutions for compact lots where ground-level footprint expansion is constrained by lot coverage limits. In Decatur&apos;s historic neighborhoods, vertical additions require careful massing to avoid dwarfing neighboring single-story cottages.
                  </p>
                  <p>
                    <strong>Structural Underpinnings:</strong> Older homes built on unreinforced brick or granite pier foundations must undergo rigorous structural analysis. Adding a second story requires verifying whether existing foundation pads can carry tributary gravity loads and lateral wind shears. In many Craftsman homes, load paths must be traced down to new interior concrete spread footers and steel flitch beams tucked within existing floor assemblies.
                  </p>
                  <p>
                    <strong>Rooflines & Scale:</strong> Preserving street-level proportions often means stepping back the second-floor wall plane from the front porch facade or adopting nested dormers, shed dormers, and complementary roof pitches that maintain horizontal continuity with Decatur&apos;s tree-lined streetscapes.
                  </p>
                </CardContent>
              </Card>

              {/* Rear Additions & Kitchen Bump-Outs */}
              <Card className="bg-card/90 border border-border/70 rounded-xl shadow-xs">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2 text-primary font-medium text-xs uppercase tracking-wider mb-1">
                    <Building className="size-4" /> Ground Footprint Expansion
                  </div>
                  <CardTitle className="font-serif text-2xl text-foreground font-normal">
                    Rear Additions & Main-Level Living Extensions
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 text-sm sm:text-base text-muted-foreground leading-relaxed">
                  <p>
                    Rear bump-outs are the preferred architectural strategy for integrating open kitchen-family rooms, sculleries, and primary main-floor suites while keeping the historic front elevation intact. This method is particularly effective for 1950s brick ranches and early 20th-century Craftsman bungalows.
                  </p>
                  <p>
                    <strong>Brick Ranches:</strong> Mid-century ranches around Decatur often have low-pitched gable or hip roofs and shallow crawlspaces. Extending rearward allows for vaulting the new living areas, creating high ceilings, clerestory windows, and direct flush-transitions to rear deck or patio grading without changing the quiet mid-century street presentation.
                  </p>
                  <p>
                    <strong>Craftsman Bungalows:</strong> A rear addition must respect original eave details, exposed rafter tails, and siding exposures. Transition zones between the original structure and the new wing are best handled through subtle transitional vestibules or changes in fenestration scale.
                  </p>
                </CardContent>
              </Card>

              {/* In-Law Suites & Accessory Spaces */}
              <Card className="bg-card/90 border border-border/70 rounded-xl shadow-xs">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2 text-primary font-medium text-xs uppercase tracking-wider mb-1">
                    <Compass className="size-4" /> Multigenerational Living
                  </div>
                  <CardTitle className="font-serif text-2xl text-foreground font-normal">
                    In-Law Suites & Accessible Attached Suites
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 text-sm sm:text-base text-muted-foreground leading-relaxed">
                  <p>
                    Creating independent living quarters within an addition requires dedicated mechanical zoning, sound isolation, and accessibility considerations. Decatur homeowners frequently incorporate curbless walk-in showers, wider doorway clearances, zero-step entryways from parking areas, and private kitchenettes or morning bars.
                  </p>
                  <p>
                    When designing attached in-law suites, acoustic separation using double-stud walls with mineral wool insulation and resilient channels ensures quiet coexistence between the main residence and the suite.
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Architectural Considerations Sidebar */}
            <div className="lg:col-span-4 space-y-6">
              <div className="p-6 rounded-xl bg-secondary/40 border border-border/80">
                <h3 className="font-serif text-lg font-medium text-foreground mb-3">
                  Key Architectural Archetypes
                </h3>
                <div className="space-y-4 text-sm text-muted-foreground">
                  <div>
                    <h4 className="font-medium text-foreground text-xs uppercase tracking-wider mb-1">
                      Craftsman Bungalows (1910s–1930s)
                    </h4>
                    <p>
                      Characterized by low-pitched roofs, wide eaves with exposed brackets, tapered porch piers, and original multi-pane double-hung windows. Additions should subordinate their volume to the primary roofline.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-medium text-foreground text-xs uppercase tracking-wider mb-1">
                      Brick Ranches (1950s–1960s)
                    </h4>
                    <p>
                      Wide horizontal layouts, solid brick veneer, and low foundation heights. Additions benefit from linear rear extensions, matching brick mortar courses, or distinct modern-transitional materials.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-medium text-foreground text-xs uppercase tracking-wider mb-1">
                      Folk Victorian & Cottages
                    </h4>
                    <p>
                      Steeper gables and narrow profiles. Vertical additions must preserve asymmetric roof features and exterior lap siding dimensions.
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-6 rounded-xl bg-card border border-border/70 shadow-xs">
                <h3 className="font-serif text-lg font-medium text-foreground mb-2">
                  Structural Readiness Check
                </h3>
                <ul className="space-y-2.5 text-xs text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                    <span>Foundation pier depth & soil bearing capacity testing</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                    <span>Electrical service capacity (upgrading to 200A or 400A)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                    <span>HVAC load calculations and multi-zone heat pump placement</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                    <span>Plumbing stack tie-ins and sewer lateral slope verification</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>}

        {/* Tab 2: Municipal Permitting & Zoning Differences */}
        {activeTab === "permitting" && <div className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* City of Decatur Card */}
              <Card className="bg-card/90 border border-border/80 rounded-xl shadow-xs">
                <CardHeader>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs uppercase tracking-wider font-semibold text-primary">Municipal Jurisdiction</span>
                    <Badge variant="secondary" className="text-xs">4-Square-Mile Boundary</Badge>
                  </div>
                  <CardTitle className="font-serif text-2xl text-foreground font-normal">
                    City of Decatur (4.2 sq mi)
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 text-sm text-muted-foreground leading-relaxed">
                  <p>
                    Properties within the incorporated City of Decatur municipal limits fall under the Unified Development Ordinance (UDO). Permitting is managed centrally through the City of Decatur Design, Environment & Construction Division.
                  </p>
                  <div className="space-y-3 pt-2">
                    <div className="p-3 bg-secondary/30 rounded-lg border border-border/50">
                      <h4 className="font-semibold text-foreground text-xs uppercase tracking-wider mb-1">
                        Floor Area Ratio (FAR) & Lot Coverage
                      </h4>
                      <p className="text-xs">
                        Strict limits govern total conditioned square footage relative to lot size (often max 0.40 FAR in R-60 single-family zoning) and impervious surface maximums (typically 40%). Additions must include accurate site surveys with impervious surface calculations.
                      </p>
                    </div>
                    <div className="p-3 bg-secondary/30 rounded-lg border border-border/50">
                      <h4 className="font-semibold text-foreground text-xs uppercase tracking-wider mb-1">
                        Tree Canopy Ordinance
                      </h4>
                      <p className="text-xs">
                        Decatur has one of the strictest tree protection ordinances in Georgia. Any addition impacting critical root zones (CRZ) of protected trees (over 15 inches DBH) requires a certified arborist report, tree preservation fencing, or calculated tree canopy replacement fees.
                      </p>
                    </div>
                    <div className="p-3 bg-secondary/30 rounded-lg border border-border/50">
                      <h4 className="font-semibold text-foreground text-xs uppercase tracking-wider mb-1">
                        Stormwater Management
                      </h4>
                      <p className="text-xs">
                        Projects adding over 500 sq ft of new impervious surface trigger mandatory onsite stormwater mitigation, such as dry wells, rain gardens, or underground retention chambers.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Unincorporated DeKalb County Card */}
              <Card className="bg-card/90 border border-border/80 rounded-xl shadow-xs">
                <CardHeader>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs uppercase tracking-wider font-semibold text-primary">County Jurisdiction</span>
                    <Badge variant="outline" className="text-xs">DeKalb County Government</Badge>
                  </div>
                  <CardTitle className="font-serif text-2xl text-foreground font-normal">
                    Unincorporated DeKalb County
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 text-sm text-muted-foreground leading-relaxed">
                  <p>
                    Homes with Decatur mailing addresses outside the city limits (such as Medlock Park, Leafmore, North Decatur, and Belvedere Park) follow DeKalb County Planning & Sustainability Department regulations.
                  </p>
                  <div className="space-y-3 pt-2">
                    <div className="p-3 bg-secondary/30 rounded-lg border border-border/50">
                      <h4 className="font-semibold text-foreground text-xs uppercase tracking-wider mb-1">
                        Setback & Height Allowances
                      </h4>
                      <p className="text-xs">
                        Standard residential classifications (R-75, R-85, R-100) dictate side setbacks (typically 7.5 to 10 feet) and rear setbacks (typically 30 to 40 feet). Lot coverage thresholds generally offer slightly broader allowable building footprints compared to City of Decatur R-60 limits.
                      </p>
                    </div>
                    <div className="p-3 bg-secondary/30 rounded-lg border border-border/50">
                      <h4 className="font-semibold text-foreground text-xs uppercase tracking-wider mb-1">
                        Plan Review & Trade Permitting
                      </h4>
                      <p className="text-xs">
                        Building plan submissions require structural drawings, foundation details, and MEP (Mechanical, Electrical, Plumbing) sub-contractor permits submitted through the DeKalb County ePermitting portal.
                      </p>
                    </div>
                    <div className="p-3 bg-secondary/30 rounded-lg border border-border/50">
                      <h4 className="font-semibold text-foreground text-xs uppercase tracking-wider mb-1">
                        Environmental & Watershed Codes
                      </h4>
                      <p className="text-xs">
                        Properties near local creek buffers (such as South Peachtree Creek or Shoal Creek tributaries) must observe state and county 75-foot stream buffer setbacks where ground disturbance is prohibited.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Jurisdiction Comparison Matrix Callout */}
            <div className="p-6 rounded-xl bg-secondary/40 border border-border">
              <div className="flex items-start gap-3">
                <ShieldAlert className="size-5 text-primary shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <h3 className="font-medium text-foreground text-sm">
                    Confirming Your Parcel Boundary Before Architectural Schematics
                  </h3>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                    Decatur postal zip codes (30030, 30032, 30033) encompass both the incorporated City of Decatur and unincorporated county parcels. A boundary confirmation on the official DeKalb GIS property mapping determines whether your addition plans proceed to the Decatur City Hall Review board or the DeKalb County Memorial Drive facility.
                  </p>
                </div>
              </div>
            </div>
          </div>}

        {/* Tab 3: Historic District Design Review Standards */}
        {activeTab === "historic" && <div className="space-y-8">
            <Card className="bg-card/90 border border-border/80 rounded-xl shadow-xs">
              <CardHeader className="pb-4">
                <div className="flex items-center gap-2 text-primary font-medium text-xs uppercase tracking-wider mb-1">
                  <Landmark className="size-4" /> Historic Preservation Commission (HPC)
                </div>
                <CardTitle className="font-serif text-2xl sm:text-3xl text-foreground font-normal">
                  Exterior Work in Decatur Local Historic Districts
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-5 text-sm sm:text-base text-muted-foreground leading-relaxed">
                <p>
                  Homes located within designated local historic districts—such as the <strong>MAK Historic District</strong> (McDonough, Adams, Kings Highway), <strong>Clairemont Avenue Historic District</strong>, <strong>Old Decatur Historic District</strong>, and <strong>Ponce de Leon Court</strong>—require a Certificate of Appropriateness (COA) from the Decatur Historic Preservation Commission prior to building permit issuance.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-2">
                  <div className="p-4 rounded-lg bg-secondary/30 border border-border/60 space-y-2">
                    <h4 className="font-medium text-foreground text-sm">Massing & Visibility</h4>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      Additions must be visually subordinate to the historic main building. Second-story expansions must be placed toward the rear, minimizing visibility from the primary public right-of-way.
                    </p>
                  </div>
                  <div className="p-4 rounded-lg bg-secondary/30 border border-border/60 space-y-2">
                    <h4 className="font-medium text-foreground text-sm">Materials & Textures</h4>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      Exterior cladding should be compatible with the historic fabric. Smooth-finish cementitious siding or wood lap siding matching original exposure dimensions is required; synthetic materials like vinyl or faux-stone are generally prohibited.
                    </p>
                  </div>
                  <div className="p-4 rounded-lg bg-secondary/30 border border-border/60 space-y-2">
                    <h4 className="font-medium text-foreground text-sm">Fenestration & Openings</h4>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      Window rhythm, proportions, and lite patterns on visible exterior elevations must harmonize with original architectural patterns. true-divided or simulated-divided lites with exterior profiles are required.
                    </p>
                  </div>
                </div>

                <div className="pt-3 border-t border-border/60">
                  <h4 className="font-serif text-lg font-medium text-foreground mb-2">
                    The Certificate of Appropriateness (COA) Process
                  </h4>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                    Staff-level approvals can often be obtained for minor exterior alterations, while substantial additions and second-story dormers require a formal public hearing before the Decatur HPC. Submissions require scaled elevation drawings showing existing versus proposed conditions, material specifications, and sightline diagrams from opposite street viewpoints.
                  </p>
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="p-5 rounded-xl bg-card border border-border/70 shadow-2xs">
                <div className="flex items-center gap-2 font-medium text-foreground text-sm mb-1.5">
                  <TreePine className="size-4 text-primary" />
                  Preserving Historic Tree Canopies
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Mature oaks, magnolias, and tulip poplars defining historic Decatur streetscapes must be safeguarded during addition excavation using non-destructive pneumatic root trenching where utilities connect.
                </p>
              </div>
              <div className="p-5 rounded-xl bg-card border border-border/70 shadow-2xs">
                <div className="flex items-center gap-2 font-medium text-foreground text-sm mb-1.5">
                  <Building className="size-4 text-primary" />
                  Differentiating Old from New
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  National Park Service preservation standards followed by the HPC encourage additions that complement original architecture while remaining distinguishable upon close inspection, avoiding false historicism.
                </p>
              </div>
            </div>
          </div>}

        {/* Closing Planning Assistance Band */}
        <div className="mt-16 p-8 rounded-2xl bg-secondary/60 border border-border flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="space-y-2 max-w-2xl">
            <h3 className="font-serif text-xl sm:text-2xl font-medium text-foreground">
              Ready to evaluate your home&apos;s addition feasibility?
            </h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Explore our structured renovation planning process or contact our team to discuss lot setbacks, zoning compliance, and architectural integration for your Decatur property.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button asChild variant="default" size="lg" className="rounded-lg text-sm font-medium">
              <Link to="/ContactPage">
                Contact Our Team
                <ArrowRight className="size-4 ml-2" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="rounded-lg text-sm font-medium bg-background">
              <Link to="/ProcessPlanningPage">
                Explore Process & Planning
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}